import { Sim, ToolId, SeedType, SEED_TYPES } from '../../game/simulation';
import { ICON } from '../icons';

/**
 * Inline plot toolbar — visible only while a plot is adopted.
 *
 * Tools are grouped by purpose (Hand / Prep / Sow / Reap / Gather) with a
 * label above each cluster. Same grouping as the world Dock so muscle
 * memory carries from overview to plot view.
 *
 * Click a tool slot to equip; click a seed slot to set selectedSeed AND
 * equip the plant tool. The active slot is highlighted; the hands slot
 * is the "drop" affordance.
 */
type ToolCategory = 'hand' | 'prep' | 'plant' | 'reap' | 'gather';
type ToolSlot = { id: ToolId | 'hands'; label: string; icon: string; group: ToolCategory };
const TOOL_SLOTS: ToolSlot[] = [
  { id: 'hands',   label: 'Hands',   icon: ICON.hands,   group: 'hand'   },
  { id: 'field',   label: 'Hoe',     icon: ICON.hoe,     group: 'prep'   },
  { id: 'water',   label: 'Water',   icon: ICON.water,   group: 'prep'   },
  { id: 'plant',   label: 'Plant',   icon: ICON.plant,   group: 'plant'  },
  { id: 'scythe',  label: 'Sickle',  icon: ICON.scythe,  group: 'reap'   },
  { id: 'axe',     label: 'Axe',     icon: ICON.axe,     group: 'gather' },
  { id: 'pickaxe', label: 'Pickaxe', icon: ICON.pickaxe, group: 'gather' },
];
const GROUP_LABELS: Record<ToolCategory, string> = {
  hand: 'Hands', prep: 'Prep', plant: 'Sow', reap: 'Reap', gather: 'Gather',
};

export class PlotToolbar {
  private root: HTMLDivElement;
  /** Last-rendered hash. Re-render only when state changes so clicks
   *  aren't torn between mousedown and mouseup by a per-frame innerHTML
   *  rewrite (this was the bug: tool slots after Hands wouldn't respond). */
  private lastHash = '';

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-plottools';
    this.root.style.display = 'none';
    parent.appendChild(this.root);
    this.root.addEventListener('click', this.onClick);
  }

  update(_dt: number) {
    const show = !!this.sim.state.plotInView;
    if (!show) {
      if (this.root.style.display !== 'none') {
        this.root.style.display = 'none';
        this.lastHash = '';      // force rebuild next time we open
      }
      return;
    }
    if (this.root.style.display === 'none') this.root.style.display = '';
    const s = this.sim.state;
    const ap = s.activePlotIdx != null ? s.plots[s.activePlotIdx] : null;
    // Cheap multi-field hash via string concat — replaces JSON.stringify
    // which was allocating ~1-2KB strings every frame for the same
    // change-detection result.
    const sd = s.res.seeds;
    const hash =
      s.tool + '|' + s.selectedSeed + '|' + (s.selectedSpecialSeed ?? '') + '|' +
      sd.wheat + ',' + sd.corn + ',' + sd.carrot + '|' +
      s.prog.level + '|' + (s.res.berries ?? 0) + '|' + (s.res.treeSeeds ?? 0) + '|' +
      (s.editingPlot ? 1 : 0) + '|' +
      (ap?.tiles.length ?? 0) + ',' + (ap?.tilled.length ?? 0) + ',' +
      (ap?.seeds.length ?? 0) + ',' + (ap?.wet.length ?? 0) + '|' +
      s.weeds.length;
    if (hash === this.lastHash) return;
    this.lastHash = hash;
    this.render();
  }

  private render() {
    const tool = this.sim.state.tool;
    const sel = this.sim.state.selectedSeed;
    const seeds = this.sim.state.res.seeds;
    const lv = this.sim.state.prog.level;
    // Progressive disclosure: filter out tools the player hasn't unlocked
    // so the toolbar grows organically. Pickaxe also waits until the
    // player has actually broken / seen stone so it doesn't tease a
    // mechanic with nothing to swing at.
    const u = this.sim.state.prog.unlocked;
    const everSeenStone = (this.sim.state.res.stone ?? 0) > 0;
    const visible = TOOL_SLOTS.filter(s => {
      if (s.id === 'hands') return true;
      if (!u[s.id]) return false;
      if (s.id === 'pickaxe' && !everSeenStone && lv < 2) return false;
      return true;
    });
    // Group tool slots by category so the toolbar reads as
    // [Hands] | [Prep] | [Sow] | [Reap] | [Gather] instead of one flat
    // row of identical-looking emoji.
    const groupOrder: ToolCategory[] = [];
    const groupBuckets = new Map<ToolCategory, ToolSlot[]>();
    for (const s of visible) {
      if (!groupBuckets.has(s.group)) {
        groupBuckets.set(s.group, []);
        groupOrder.push(s.group);
      }
      groupBuckets.get(s.group)!.push(s);
    }
    const toolHTML = groupOrder.map(group => {
      const items = groupBuckets.get(group)!;
      const slots = items.map(s => {
        const active = (tool === 'hands' && s.id === 'hands') || (tool !== 'hands' && tool === s.id);
        return `
          <button class="ui-plottools__slot ${active ? 'active' : ''}" data-tool="${s.id}" title="${s.label}">
            <span class="ui-plottools__glyph glyph">${s.icon}</span>
            <span class="ui-plottools__label">${s.label}</span>
          </button>
        `;
      }).join('');
      // Single-slot groups skip the label (the button caption is enough).
      const labelHTML = items.length > 1
        ? `<div class="ui-plottools__group-label">${GROUP_LABELS[group]}</div>`
        : '';
      return `
        <div class="ui-plottools__group" data-group="${group}">
          ${labelHTML}
          <div class="ui-plottools__group-row">${slots}</div>
        </div>
      `;
    }).join('');
    const seedGlyph: Record<SeedType, string> = { wheat: ICON.wheat, corn: ICON.corn, carrot: ICON.carrot };
    const special = this.sim.state.selectedSpecialSeed ?? null;
    const seedHTML = SEED_TYPES.map(seed => {
      const locked = seed !== 'wheat' && lv < 2;
      const active = tool === 'plant' && sel === seed && special === null;
      const count = seeds[seed] ?? 0;
      return `
        <button class="ui-plottools__slot ${active ? 'active' : ''} ${locked ? 'locked' : ''}" data-seed="${seed}" ${locked ? 'disabled' : ''}>
          <span class="ui-plottools__glyph glyph">${seedGlyph[seed]}</span>
          <span class="ui-plottools__label">${seed}${locked ? '' : ` ×${count}`}</span>
        </button>
      `;
    }).join('');
    // Forage seeds — berries → bush, acorns → tree. Plant tool plants
    // these at TILE level (no sub-cell drag) on a cleared grass plot tile.
    const berryCount = this.sim.state.res.berries ?? 0;
    const acornCount = this.sim.state.res.treeSeeds ?? 0;
    const specialHTML = [
      { id: 'berry', label: 'Berry', icon: ICON.berry, count: berryCount },
      { id: 'acorn', label: 'Acorn', icon: ICON.acorn, count: acornCount },
    ].map(s => {
      const empty = s.count <= 0;
      const active = tool === 'plant' && special === s.id;
      return `
        <button class="ui-plottools__slot ${active ? 'active' : ''} ${empty ? 'locked' : ''}" data-special="${s.id}" ${empty ? 'disabled' : ''}>
          <span class="ui-plottools__glyph glyph">${s.icon}</span>
          <span class="ui-plottools__label">${s.label} ×${s.count}</span>
        </button>
      `;
    }).join('');
    // Plot progress lives in the InfoCard now; tile edits live in the
    // Zones manager. Plot view itself owns nothing custom in the toolbar.
    void toolHTML; void seedHTML; void specialHTML;
    this.root.innerHTML = '';
  }

  private onClick = (e: Event) => {
    const t = e.target as HTMLElement;
    const toolEl    = t.closest('[data-tool]')    as HTMLButtonElement | null;
    const seedEl    = t.closest('[data-seed]')    as HTMLButtonElement | null;
    const specialEl = t.closest('[data-special]') as HTMLButtonElement | null;
    const actEl     = t.closest('[data-act]')     as HTMLButtonElement | null;
    if (actEl) {
      if (actEl.dataset.act === 'toggle-edit') {
        this.sim.togglePlotEdit();
      }
      return;
    }
    if (toolEl) {
      const id = toolEl.dataset.tool as ToolId | 'hands';
      this.sim.setTool(id === 'hands' ? 'hands' : id);
      // Switching to a non-plant tool clears the special-seed routing so
      // it doesn't lurk for the next plant click.
      if (id !== 'plant') this.sim.setSelectedSpecialSeed(null);
      return;
    }
    if (specialEl && !specialEl.disabled) {
      const kind = specialEl.dataset.special as 'berry' | 'acorn';
      // Toggle: clicking the active special clears it.
      const cur = this.sim.state.selectedSpecialSeed;
      this.sim.setSelectedSpecialSeed(cur === kind ? null : kind);
      this.sim.setTool('plant');
      return;
    }
    if (seedEl && !seedEl.disabled) {
      const seed = seedEl.dataset.seed as SeedType;
      this.sim.setSelectedSeed(seed);
      this.sim.setTool('plant');
    }
  };
}
